"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { valuesWithAppName } from "@/lib/i18n";
import {
  estimateLocationFromMap,
  formatLocation,
} from "@/lib/locations";
import { api } from "@/lib/api-client";
import { getMapComponents, type MapClient } from "@repo/maps";
import { useAtom } from "jotai";
import { CheckCircle2Icon } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { type RefObject, useMemo, useState } from "react";
import { Sheet } from "react-modal-sheet";
import { toast } from "sonner";
import Turnstile, { useTurnstile } from "react-turnstile";

export type NotifyWaitlistSheetProps = {
  mapRef: RefObject<MapClient | null>;
};

export function NotifyWaitlistSheet({ mapRef }: NotifyWaitlistSheetProps) {
  const t = useTranslations();
  const { user } = useCurrentUser();

  const [filters] = useAtom(businessFiltersAtom);
  const [open, setOpen] = useState(false);
  const [visitorEmail, setVisitorEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();

  const { useMapSearch } = getMapComponents(
    process.env.NEXT_PUBLIC_MAPS_PROVIDER as any,
  );

  const mapSearchHook = useMapSearch({
    token: process.env.NEXT_PUBLIC_MAP_TOKEN as string,
  });

  const turnstile = useTurnstile();

  const recordWaitlistInterestMutation =
    api.listings.recordWaitlistInterest.useMutation();

  const { displayErrorToast } = useErrorFormatter();

  const locationName = useMemo(() => {
    if (!filters.location) return null;
    return formatLocation(filters.location);
  }, [filters.location]);

  async function handleSubmit() {
    if (subscribed) return;

    let location = filters.location;

    if (!location && mapRef.current) {
      location = await estimateLocationFromMap(mapRef.current, mapSearchHook);
    }

    if (!location) {
      toast.error(t("errors.recordWaitlistInterest.locationCannotBeInferred"));
      return;
    }

    const context = location.context;
    const countryName = context?.country?.name;
    const countryCode = context?.country?.code;
    const city = context?.place?.name;
    const county = context?.district?.name;
    const state = context?.region?.name;
    const email = user ? undefined : visitorEmail;

    if (!countryName && !countryCode) {
      toast.error(t("errors.recordWaitlistInterest.locationCannotBeInferred"));
      return;
    }

    if (!user && !email) {
      toast.error(t("errors.recordWaitlistInterest.locationCannotBeInferred"));
      return;
    }

    try {
      await recordWaitlistInterestMutation.mutateAsync({
        email,
        captchaToken,
        countryName,
        countryCode,
        city,
        county,
        state,
      });

      setSubscribed(true);
    } catch (err) {
      displayErrorToast(err);
    } finally {
      if (turnstile) {
        turnstile.reset();
      }
    }
  }

  if (subscribed) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="gap-2 text-theme-emerald"
      >
        <CheckCircle2Icon className="h-4 w-4" />
        {t("saas.search.zds.noFilters.buttonSubmitted")}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => setOpen(true)}
      >
        <Icon icon="BellPlus" className="h-4 w-4" />
        {t("saas.search.zds.noFilters.button")}
      </Button>

      <Sheet isOpen={open} onClose={() => setOpen(false)}>
        <Sheet.Backdrop onTap={() => setOpen(false)} />
        <Sheet.Container className="rounded-t-2xl">
          <Sheet.Header />

          <div className="border-b border-border px-4 pb-3 pt-2">
            <div className="flex items-center justify-between gap-3">
              <span className="w-9" />
              <h2 className="truncate text-base font-semibold text-foreground">
                {t("saas.search.zds.noFilters.notifyTitle")}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border"
                aria-label="Close"
              >
                <Icon icon="XClose" className="h-4 w-4" />
              </button>
            </div>
          </div>

          <Sheet.Content disableDrag>
            <div className="flex flex-col px-5 pt-5 pb-8">
              <div className="flex flex-col items-center text-center gap-3 mb-6">
                <div className="rounded-full bg-muted p-3">
                  <Icon icon="BellPlus" className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      user
                        ? "saas.search.zds.noFilters.description.loggedIn"
                        : "saas.search.zds.noFilters.description.loggedOut",
                    )}
                  </p>
                  {locationName && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {locationName}
                    </p>
                  )}
                </div>
              </div>

              {!user && (
                <div className="flex flex-col gap-3 mb-4">
                  <Input
                    type="email"
                    value={visitorEmail}
                    onChange={(e) => setVisitorEmail(e.target.value)}
                    placeholder={t(
                      "saas.search.zds.noFilters.form.email.placeholder",
                    )}
                    className="bg-card"
                    autoFocus
                  />

                  <Turnstile
                    sitekey={
                      process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY as string
                    }
                    onVerify={(token) => setCaptchaToken(token)}
                  />

                  <p className="text-xs text-muted-foreground">
                    {t.rich(
                      "saas.search.zds.noFilters.legalDisclaimer",
                      valuesWithAppName(t, {
                        terms: (children) => (
                          <Link href="/terms" className="underline">
                            {children}
                          </Link>
                        ),
                        privacy: (children) => (
                          <Link href="/privacy" className="underline">
                            {children}
                          </Link>
                        ),
                      }),
                    )}
                  </p>
                </div>
              )}

              <Button
                className="h-11 w-full"
                disabled={
                  recordWaitlistInterestMutation.isPending ||
                  (!user && !visitorEmail)
                }
                onClick={async () => {
                  await handleSubmit();
                  if (!recordWaitlistInterestMutation.isError) {
                    setOpen(false);
                  }
                }}
              >
                <Icon icon="BellPlus" className="h-4 w-4" />
                {t("saas.search.zds.noFilters.button")}
              </Button>
            </div>
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </>
  );
}

NotifyWaitlistSheet.displayName = "NotifyWaitlistSheet";
